import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-keys";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useFilterSearchParams } from "@/hooks/use-filter-search-params";
import { useUrlSelection } from "@/hooks/use-url-selection";
import {
  usePagination,
  usePageOffset,
  DEFAULT_PAGE_SIZE,
} from "@/hooks/use-pagination";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { EmptyState, ErrorState, Text } from "@/components/ui/text";
import { FilterChip } from "@/components/ui/filter-chip";
import { FilterToolbar } from "@/components/ui/filter-toolbar";
import { Icon } from "@/components/ui/icons";
import { LoadId } from "@/components/ui/load-id";
import { PageLoadingOverlay } from "@/components/ui/page-loading-overlay";
import { PageTitle } from "@/contexts/page-title-context";
import { EntityStatusBadge } from "@/components/shared/entity-status-badge";
import { TableSkeleton } from "@/components/ui/table-skeleton";
import { TableViewport } from "@/components/ui/table-viewport";
import { InteractiveCard } from "@/components/ui/interactive-card";
import { CompactCardSkeleton } from "@/components/ui/compact-card-skeleton";
import { StickyHeaderSlot } from "@/components/ui/sticky-header-slot";
import { VStack } from "@/components/ui/v-stack";
import { HStack } from "@/components/ui/h-stack";
import { DividedList } from "@/components/ui/divided-list";
import { useSizeClass } from "@/hooks/use-size-class";
import { fromIntlLanguage } from "@/lib/i18n/config";
import { highlight, highlightNumber } from "@/lib/highlight";
import { loadSearchableFields } from "@/lib/search/load";
import { formatDate, formatTonnage, type UnitLabels } from "@/lib/formatting";
import { LoadListTable } from "@/components/loads/load-list-table";
import { getAdminLoads } from "@/lib/api/admin/loads";

type StatusFilter = "All" | "Pending" | "Approved";

function AdminLoadApprovalsPage() {
  const sizeClass = useSizeClass();
  const isCompact = sizeClass === "compact";
  const search = useSearch({ strict: false }) as { refresh?: string };
  const navigate = useNavigate();
  const language = fromIntlLanguage(useTranslation().i18n.language);
  const { t: tAdminLoadApprovals } = useTranslation("admin.loadApprovals");
  const { t: tCommon } = useTranslation("common");
  const units: UnitLabels = {
    tonnageUnit: tCommon("tonnageUnit"),
    volumeUnit: tCommon("volumeUnit"),
  };

  const refreshToken = search.refresh ?? "";

  const { filters, setFilter } = useFilterSearchParams({
    keys: ["search", "status"],
    defaults: { status: "All" },
  });
  const searchQuery = filters.search;
  // Map legacy "PendingApproval" param from dashboard deep-links
  const statusFilter = (
    filters.status === "PendingApproval" ? "Pending" : filters.status
  ) as StatusFilter;

  // FilterToolbar already debounces draft → URL commits at 300ms, so
  // searchQuery here is the committed (debounced) value.
  const q = useMemo(() => {
    const trimmed = searchQuery.trim();
    return trimmed.length >= 3 ? trimmed : undefined;
  }, [searchQuery]);

  const { offset } = usePageOffset();
  const {
    data,
    isLoading: loading,
    isError,
    refetch,
  } = useQuery({
    queryKey: [
      ...queryKeys.admin.loads({ limit: DEFAULT_PAGE_SIZE, offset, q }),
      refreshToken,
    ],
    queryFn: () => getAdminLoads({ limit: DEFAULT_PAGE_SIZE, offset, q }),
  });
  const loads = useMemo(() => data?.data ?? [], [data]);
  const total = data?.total ?? 0;

  const { page, totalPages, setPage } = usePagination({ total });

  const filteredLoads = useMemo(() => {
    return loads.filter((l) => {
      if (statusFilter === "All") return true;
      if (statusFilter === "Pending") return l.status === "PendingApproval";
      return l.status !== "PendingApproval" && l.status !== "Declined";
    });
  }, [loads, statusFilter]);

  const { selectedId: selectedAdminLoadId, select: selectAdminLoad } =
    useUrlSelection("selectedAdminLoadId", filteredLoads, loading);

  const statusOptions = [
    { value: "All", label: tAdminLoadApprovals("status.all") },
    { value: "Pending", label: tAdminLoadApprovals("status.pending") },
    { value: "Approved", label: tAdminLoadApprovals("status.approved") },
  ];

  return (
    <VStack gap="lg" data-testid="admin-loads-page">
      <PageTitle>{tAdminLoadApprovals("title")}</PageTitle>
      <StickyHeaderSlot data-testid="admin-loads-header">
        <Card
          data-testid="admin-loads-header-actions-card"
          variant="bordered"
          padding="xs"
        >
          <FilterToolbar
            searchPlaceholder={tAdminLoadApprovals("searchPlaceholder")}
            searchValue={searchQuery}
            onSearchChange={(v) => setFilter("search", v)}
            pagination={{
              page,
              totalPages,
              perPage: DEFAULT_PAGE_SIZE,
              onPageChange: setPage,
              loading,
            }}
            quickFilters={
              <>
                {statusOptions.map((opt) => (
                  <FilterChip
                    key={opt.value}
                    active={statusFilter === opt.value}
                    onClick={() => setFilter("status", opt.value)}
                    data-testid={`admin-loads-filter-status-${opt.value}`}
                  >
                    {opt.label}
                  </FilterChip>
                ))}
              </>
            }
            data-testid="admin-loads-toolbar"
          />
        </Card>
      </StickyHeaderSlot>

      <Card data-testid="admin-loads-card" padding="none">
        <TableViewport position="relative">
          {loading && loads.length === 0 ? (
            isCompact ? (
              <CompactCardSkeleton />
            ) : (
              <TableSkeleton columns={6} rows={5} />
            )
          ) : isError ? (
            <ErrorState
              title={tAdminLoadApprovals("failedToLoadLoads")}
              action={
                <Button
                  color="neutral"
                  variant="outline"
                  onClick={() => void refetch()}
                >
                  {tCommon("retry")}
                </Button>
              }
              className="py-16"
            />
          ) : filteredLoads.length === 0 ? (
            <EmptyState
              icon={<Icon.Search size="xl" />}
              title={tAdminLoadApprovals("noLoadsFound")}
              className="py-16"
            />
          ) : isCompact ? (
            <DividedList>
              {filteredLoads.map((load) => {
                const fields = loadSearchableFields(load, language);
                const originLabel = fields.stop_origin_city.value
                  ? fields.stop_origin_city
                  : fields.country_origin;
                const destLabel = fields.stop_destination_city.value
                  ? fields.stop_destination_city
                  : fields.country_destination;
                return (
                  <InteractiveCard
                    key={load.id}
                    data-testid={`admin-load-card-${load.id}`}
                    onClick={() => {
                      selectAdminLoad(load.id);
                      void navigate({
                        to: "/admin/loads/$id",
                        params: { id: load.id },
                      });
                    }}
                  >
                    <HStack justify="between" gap="none">
                      <LoadId>{highlight(fields.load_id, q)}</LoadId>
                      <EntityStatusBadge
                        entityType="load"
                        status={load.status}
                        searchable={fields.status}
                        q={q}
                      />
                    </HStack>
                    <Text>
                      {originLabel.value ? highlight(originLabel, q) : "—"} →{" "}
                      {destLabel.value ? highlight(destLabel, q) : "—"}
                    </Text>
                    <Text size="xs" color="muted">
                      {formatDate(load.load_date, language)}
                    </Text>
                    <HStack justify="between" gap="none">
                      <Text size="xs" color="muted">
                        {highlight(fields.load_type, q)}
                        {" · "}
                        {formatTonnage(load.tonnage, units, language)}
                      </Text>
                      <Text weight="semibold">
                        {load.currency?.symbol ?? ""}
                        {highlightNumber(fields.price, q)}
                      </Text>
                    </HStack>
                    <Text size="xs" color="muted">
                      {fields.shipper.value
                        ? highlight(fields.shipper, q)
                        : "—"}
                    </Text>
                  </InteractiveCard>
                );
              })}
            </DividedList>
          ) : (
            <LoadListTable
              loads={filteredLoads}
              q={searchQuery}
              selectedLoadId={selectedAdminLoadId}
              showTripProgress={true}
              showOffers={true}
              getLoadHref={(load) => `/admin/loads/${load.id}`}
              getOffersHref={(load) => `/admin/loads/${load.id}/trades`}
              onRowClick={(load) => {
                selectAdminLoad(load.id);
              }}
              actions={[
                {
                  key: "view",
                  label: tCommon("view"),
                  color: "neutral",
                  variant: "outline",
                },
              ]}
              onActionClick={(action, load) => {
                if (action === "view") {
                  selectAdminLoad(load.id);
                  void navigate({
                    to: "/admin/loads/$id",
                    params: { id: load.id },
                  });
                }
              }}
            />
          )}
          {loading && loads.length > 0 && (
            <PageLoadingOverlay
              page={page}
              label={tCommon("pagination.loadingPage")}
            />
          )}
        </TableViewport>
      </Card>
    </VStack>
  );
}

export const Route = createFileRoute("/_app/admin/loads/")({
  component: AdminLoadApprovalsPage,
});
